import { useEffect, useState } from "react";
import axios from "axios";
import PublicLayout from "../../components/marketing/PublicLayout";
import { useMarketingLang } from "../../lib/useMarketingLang";
import { CalendarDays, Users, Ship, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Booking-siden: viser 7-dagers ledighet fra /api/public/availability
// (samme datakilde som selve appen bruker), og lar besøkende sende en
// booking-forespørsel som havner rett i admin-arbeidsflyten på
// canalboats.me. Full Stripe-betaling er planlagt neste steg.
export default function MarketingBook() {
  const { t } = useMarketingLang();
  const [avail, setAvail] = useState(null);
  const [form, setForm] = useState({
    start_date: "", end_date: "", guests: 2, boat_id: "",
    name: "", email: "", phone: "", message: "",
  });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/public/availability`)
      .then((r) => alive && setAvail(r.data))
      .catch(() => alive && setAvail({ boats_in: [], boats_out: [], week_availability: [] }));
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      await axios.post(`${API}/public/booking-request`, form);
      setStatus("ok");
      setForm({ start_date: "", end_date: "", guests: 2, boat_id: "", name: "", email: "", phone: "", message: "" });
    } catch {
      setStatus("err");
    } finally {
      setBusy(false);
    }
  };

  const boats = avail
    ? [...(avail.boats_in || []), ...(avail.boats_out || [])]
    : [];

  return (
    <PublicLayout>
      <section className="bg-[#0F1A2B] text-white py-14">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.42em] text-[#ECC974] mb-3">{t.book.eyebrow}</div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-3">{t.book.title}</h1>
          <p className="text-white/75">{t.book.sub}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid lg:grid-cols-5 gap-8">
        <form onSubmit={submit} className="lg:col-span-3 rounded-2xl border border-[#A57F39]/20 bg-white shadow-sm p-6 lg:p-8 space-y-4" data-testid="book-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field icon={CalendarDays} label={t.book.startDate} type="date" required value={form.start_date} onChange={change("start_date")} testid="book-start" />
            <Field icon={CalendarDays} label={t.book.endDate} type="date" required value={form.end_date} onChange={change("end_date")} testid="book-end" />
            <Field icon={Users} label={t.book.guests} type="number" min={1} max={10} required value={form.guests} onChange={change("guests")} testid="book-guests" />
            <div>
              <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 flex items-center gap-1.5">
                <Ship className="h-3.5 w-3.5" /> {t.book.boat}
              </label>
              <select
                data-testid="book-boat"
                value={form.boat_id}
                onChange={change("boat_id")}
                className="w-full rounded-lg border border-[#A57F39]/25 focus:border-[#A57F39] focus:outline-none px-3 py-2.5 text-[#0F1A2B] bg-white"
              >
                <option value="">{t.book.any}</option>
                {boats.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-[#A57F39]/15">
            <Field label={t.book.name} required value={form.name} onChange={change("name")} testid="book-name" />
            <Field label={t.book.email} type="email" required value={form.email} onChange={change("email")} testid="book-email" />
            <Field label={t.book.phone} value={form.phone} onChange={change("phone")} testid="book-phone" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block">{t.book.message}</label>
            <textarea
              data-testid="book-message"
              rows={4}
              value={form.message}
              onChange={change("message")}
              className="w-full rounded-lg border border-[#A57F39]/25 focus:border-[#A57F39] focus:outline-none px-3 py-2.5 text-[#0F1A2B]"
            />
          </div>

          {status === "ok" && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 text-sm flex items-start gap-2" data-testid="book-sent">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> {t.book.success}
            </div>
          )}
          {status === "err" && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" data-testid="book-err">
              {t.book.error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="book-submit"
            className="inline-flex items-center gap-2 rounded-full bg-[#A57F39] hover:bg-[#BD984D] disabled:opacity-60 transition text-white uppercase tracking-widest text-xs font-bold px-6 py-3"
          >
            <Send className="h-4 w-4" /> {busy ? "…" : t.book.submit}
          </button>
        </form>

        <aside className="lg:col-span-2 rounded-2xl bg-[#F8F7F2] border border-[#A57F39]/20 p-6 self-start">
          <h3 className="text-sm uppercase tracking-[0.28em] font-bold text-[#A57F39] mb-4">{t.book.availableTitle}</h3>
          {!avail && <div className="text-sm text-[#0F1A2B]/60">{t.book.loading}</div>}
          {avail && (
            <ul className="space-y-3">
              {boats.length === 0 && (
                <li className="text-sm text-[#0F1A2B]/60">—</li>
              )}
              {boats.map((b) => {
                const wa = (avail.week_availability || []).find((w) => w.id === b.id);
                const freeCount = wa ? (wa.available || []).length : 0;
                return (
                  <li key={b.id} className="flex items-center justify-between text-sm text-[#0F1A2B]">
                    <div className="flex items-center gap-2 truncate">
                      <Ship className="h-4 w-4 text-[#A57F39] shrink-0" />
                      <span className="truncate">{b.name}</span>
                    </div>
                    <span className={`text-xs font-mono ${freeCount === 0 ? "text-red-500" : "text-emerald-600"}`}>
                      {freeCount}/7 dager
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </section>
    </PublicLayout>
  );
}

function Field({ label, icon: Icon, required, type = "text", value, onChange, testid, min, max }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5" />} {label}{required && " *"}
      </label>
      <input
        data-testid={testid}
        required={required}
        type={type}
        min={min}
        max={max}
        value={value}
        onChange={onChange}
        className="w-full rounded-lg border border-[#A57F39]/25 focus:border-[#A57F39] focus:outline-none px-3 py-2.5 text-[#0F1A2B]"
      />
    </div>
  );
}
